import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isThisMonth,
  isToday,
  isTomorrow,
} from "date-fns";
import * as React from "react";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useHangarsState } from "../../../containers/HangarsStateContainer";
import { useRampsState } from "../../../containers/RampsStateContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { usePermissions } from "../../../hooks/usePermissions";
import { PlacementService } from "../../../services/PlacementService";
import { TripService } from "../../../services/TripService";
import { TripWithTenant } from "../../../types";
import { Permission } from "../../../utils/permissions";
import { AssignTripButton } from "../../../widgets/AssignTripButton";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog/ConfirmationDialog";
import { CreateTripDialog } from "../../Schedule/CreateTripDialog/CreateTripDialog";
import { BulkActionToolbar } from "./components/BulkActionToolbar";
import { useOptimisticTrips } from "./hooks/useOptimisticTrips";
import { useTripsTableData } from "./hooks/useTripsTableData";

interface AllTripsTabProps {
  locationId: string;
  locationType: "hangar" | "ramp";
  currentTime: Date;
  onAssignmentComplete?: () => void;
  onCancel?: () => void;
}

interface TripWithPlacement extends TripWithTenant {
  hangar_id?: string | null;
  ramp_id?: string | null;
  hangar_name?: string | null;
  ramp_name?: string | null;
  location_name?: string | null;
  location_type?: string | null;
}

export const AllTripsTab: React.FC<AllTripsTabProps> = ({
  locationId,
  locationType,
  currentTime,
  onAssignmentComplete,
}) => {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const { hangars } = useHangarsState();
  const { ramps } = useRampsState();
  const snackbar = useSnackbar();
  const { hasPermission } = usePermissions();

  const [loading, setLoading] = React.useState(true);
  const [initialTrips, setInitialTrips] = React.useState<TripWithPlacement[]>(
    []
  );
  const [filterText, setFilterText] = React.useState("");
  const [locationFilterId, setLocationFilterId] = React.useState<string>("");
  const [showPastTrips, setShowPastTrips] = React.useState(true);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [createTripDialogOpen, setCreateTripDialogOpen] = React.useState(false);
  const [selectedTailNumberForNewTrip, setSelectedTailNumberForNewTrip] =
    React.useState<TripWithPlacement | null>(null);
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false);

  const tripService = React.useMemo(
    () => new TripService(postgrest),
    [postgrest]
  );

  const placementService = React.useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );

  // Use optimistic trips hook
  const {
    trips,
    loadingTripIds,
    assignTrip,
    bulkAssignTrips,
    bulkUnassignTrips,
    bulkCancelTrips,
    bulkDeleteTrips,
    refreshTrips,
  } = useOptimisticTrips({ initialTrips });

  // Transform to table data
  const tableData = useTripsTableData({
    trips,
    filterText,
    locationFilterId,
    showPastTrips,
    currentTime,
    startDate,
    endDate,
  });

  // Fetch all trips on mount
  React.useEffect(() => {
    const fetchTrips = async () => {
      if (!activeFBO) return;

      try {
        setLoading(true);
        const allTrips = await tripService.listTripsWithTenantAndPlacement(
          activeFBO.id
        );

        const sortedTrips = allTrips.sort((a, b) => {
          const aDate = a.arrival_date ? new Date(a.arrival_date).getTime() : 0;
          const bDate = b.arrival_date ? new Date(b.arrival_date).getTime() : 0;
          return aDate - bDate;
        });

        setInitialTrips(sortedTrips);
        refreshTrips(sortedTrips);
      } catch (error) {
        console.error("Failed to fetch trips:", error);
        snackbar.notify({
          text: "Failed to load trips",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, [activeFBO, tripService, snackbar, refreshTrips]);

  const getTripStatus = (
    trip: TripWithPlacement
  ): "upcoming" | "active" | "past" => {
    if (!trip.arrival_date || !trip.departure_date) return "upcoming";

    const start = new Date(trip.arrival_date);
    const end = new Date(trip.departure_date);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "upcoming";

    if (currentTime < start) return "upcoming";
    if (currentTime > end) return "past";
    return "active";
  };

  const formatTripDuration = (
    startTime: string | null | undefined,
    endTime: string | null | undefined
  ) => {
    if (!startTime || !endTime) return "--";

    const start = new Date(startTime);
    const end = new Date(endTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "--";

    const days = differenceInDays(end, start);
    const hours = differenceInHours(end, start) % 24;
    const minutes = differenceInMinutes(end, start) % 60;

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const isAssignedToThisLocation = (trip: TripWithPlacement): boolean => {
    if (locationType === "hangar") {
      return trip.hangar_id === locationId;
    } else if (locationType === "ramp") {
      return trip.ramp_id === locationId;
    }
    return false;
  };

  const getStatusLabel = (
    status: "upcoming" | "active" | "past",
    trip: TripWithPlacement
  ): string => {
    if (trip.status === "cancelled") {
      return "Cancelled";
    }

    if (status === "past") {
      return "Past";
    }

    if (status === "active") {
      return "Active";
    }

    if (!trip.arrival_date) {
      return "Future";
    }

    const arrivalDate = new Date(trip.arrival_date);
    const daysDiff = differenceInDays(arrivalDate, currentTime);

    if (isToday(arrivalDate)) {
      return "Today";
    }

    if (isTomorrow(arrivalDate)) {
      return "Tomorrow";
    }

    if (daysDiff <= 7) {
      return "This Week";
    }

    if (daysDiff <= 14) {
      return "Next Week";
    }

    if (isThisMonth(arrivalDate)) {
      return "This Month";
    }

    return "Future";
  };

  // Handle single trip assignment
  const handleAssignTrip = async (
    tripId: string,
    locationId: string,
    locationType: "hangar" | "ramp",
    locationName: string
  ) => {
    const trip = trips.find((t) => t.trip_id === tripId);
    if (!trip) return;

    await assignTrip(tripId, locationId, locationType, locationName, async () => {
      await placementService.assignToLocationWithGapFilling(
        tripId,
        locationId,
        locationType,
        trip.arrival_date || "",
        trip.departure_date || "",
        true,
        { x: null, y: null, angle: null }
      );
    });

    snackbar.notify({
      text: `Assigned ${trip.tail_number} to ${locationName}`,
      severity: "success",
    });

    if (onAssignmentComplete) {
      onAssignmentComplete();
    }
  };

  // Handle bulk operations
  const handleBulkAssign = async () => {
    // Validate that a location is selected in the filter
    if (!locationFilterId) {
      snackbar.notify({
        text: "Please select a location from the filter dropdown before assigning trips",
        severity: "error",
      });
      return;
    }

    const tripIds = rowSelectionModel as string[];
    const targetLocation = allLocations.find((loc) => loc.id === locationFilterId);

    if (!targetLocation) {
      snackbar.notify({
        text: "Selected location not found",
        severity: "error",
      });
      return;
    }

    try {
      await bulkAssignTrips(
        tripIds,
        targetLocation.id,
        targetLocation.type,
        targetLocation.name,
        async (tripId) => {
          const trip = trips.find((t) => t.trip_id === tripId);
          if (!trip) return;

          await placementService.assignToLocationWithGapFilling(
            tripId,
            targetLocation.id,
            targetLocation.type,
            trip.arrival_date || "",
            trip.departure_date || "",
            true,
            { x: null, y: null, angle: null }
          );
        }
      );

      snackbar.notify({
        text: `Assigned ${tripIds.length} trip${tripIds.length !== 1 ? "s" : ""} to ${targetLocation.name}`,
        severity: "success",
      });

      setRowSelectionModel([]);

      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (error: any) {
      snackbar.notify({
        text: error.message || "Failed to assign trips",
        severity: "error",
      });
    }
  };

  const handleBulkUnassign = async () => {
    const tripIds = rowSelectionModel as string[];

    try {
      await bulkUnassignTrips(tripIds, async (tripId) => {
        await placementService.unassignFromLocation(tripId);
      });

      snackbar.notify({
        text: `Unassigned ${tripIds.length} trip${tripIds.length !== 1 ? "s" : ""}`,
        severity: "success",
      });

      setRowSelectionModel([]);

      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (error: any) {
      snackbar.notify({
        text: error.message || "Failed to unassign trips",
        severity: "error",
      });
    }
  };

  const handleBulkCancel = async () => {
    const tripIds = rowSelectionModel as string[];

    try {
      await bulkCancelTrips(tripIds, async (tripId) => {
        await tripService.cancelTrip(tripId);
      });

      snackbar.notify({
        text: `Cancelled ${tripIds.length} trip${tripIds.length !== 1 ? "s" : ""}`,
        severity: "success",
      });

      setRowSelectionModel([]);

      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (error: any) {
      snackbar.notify({
        text: error.message || "Failed to cancel trips",
        severity: "error",
      });
    }
  };

  const handleBulkDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    const tripIds = rowSelectionModel as string[];

    try {
      await bulkDeleteTrips(tripIds, async (tripId) => {
        await tripService.deleteTrip(tripId);
      });

      snackbar.notify({
        text: `Deleted ${tripIds.length} trip${tripIds.length !== 1 ? "s" : ""}`,
        severity: "success",
      });

      setRowSelectionModel([]);

      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (error: any) {
      snackbar.notify({
        text: error.message || "Failed to delete trips",
        severity: "error",
      });
    }
  };

  // Column definitions
  const columns = React.useMemo<GridColDef<TripWithPlacement>[]>(
    () => [
      {
        field: "tail_number",
        headerName: "Tail Number",
        width: 110,
        renderCell: (params) => (
          <Typography variant="caption">
            {params.row.tail_number || "--"}
          </Typography>
        ),
      },
      {
        field: "arrival_date",
        headerName: "Arrival",
        width: 130,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FlightLandIcon sx={{ fontSize: 14 }} color="action" />
            <Typography variant="caption">
              {params.row.arrival_date
                ? format(new Date(params.row.arrival_date), "MMM d HH:mm")
                : "--"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "departure_date",
        headerName: "Departure",
        width: 130,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FlightTakeoffIcon sx={{ fontSize: 14 }} color="action" />
            <Typography variant="caption">
              {params.row.departure_date
                ? format(new Date(params.row.departure_date), "MMM d HH:mm")
                : "--"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "duration",
        headerName: "Duration",
        width: 70,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="caption" color="text.secondary">
            {formatTripDuration(params.row.arrival_date, params.row.departure_date)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 85,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const status = getTripStatus(params.row);
          return (
            <Chip
              label={getStatusLabel(status, params.row)}
              size="small"
              color={
                params.row.status === "cancelled"
                  ? "error"
                  : status === "active"
                    ? "success"
                    : status === "upcoming"
                      ? "primary"
                      : "default"
              }
              variant="outlined"
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          );
        },
      },
      {
        field: "location_name",
        headerName: "Location",
        width: 150,
        renderCell: (params) => {
          const isAssigned = isAssignedToThisLocation(params.row);
          return (
            <Chip
              label={params.row.location_name || "Unassigned"}
              size="small"
              variant="filled"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                backgroundColor: isAssigned
                  ? "#e3f2fd"
                  : params.row.location_name
                    ? "#fff3e0"
                    : "#f5f5f5",
                color: isAssigned
                  ? "#1565c0"
                  : params.row.location_name
                    ? "#e65100"
                    : "#666666",
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const status = getTripStatus(params.row);
          const isAssigned = isAssignedToThisLocation(params.row);
          return (
            <AssignTripButton
              trip={params.row}
              currentLocationId={locationId}
              currentLocationType={locationType}
              isAssigned={isAssigned}
              disabled={status === "past" || params.row.status === "cancelled"}
              size="small"
              iconOnly={true}
              onAssignmentComplete={onAssignmentComplete}
            />
          );
        },
      },
    ],
    [
      currentTime,
      locationId,
      locationType,
      onAssignmentComplete,
    ]
  );

  // Locations for bulk action toolbar
  const allLocations = React.useMemo(() => {
    const locations: Array<{
      id: string;
      name: string;
      type: "hangar" | "ramp";
    }> = [];

    hangars.forEach((hangar) => {
      locations.push({
        id: hangar.id,
        name: hangar.name,
        type: "hangar",
      });
    });

    ramps.forEach((ramp) => {
      locations.push({
        id: ramp.id,
        name: ramp.name,
        type: "ramp",
      });
    });

    return locations;
  }, [hangars, ramps]);

  // Get selected trip count
  const selectedTripCount = rowSelectionModel.length;

  if (loading) {
    return (
      <Stack spacing={2} sx={{ minHeight: 400, p: 2 }}>
        <Skeleton variant="text" height={40} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      sx={{
        minHeight: 300,
        maxHeight: 600,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search/Filter Bar */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder="Filter tail or owner"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ width: 200 }}
          />
          <Select
            size="small"
            value={locationFilterId}
            onChange={(e) => setLocationFilterId(e.target.value)}
            displayEmpty
            sx={{ width: 180, bgcolor: "background.paper" }}
          >
            <MenuItem value="">
              <em>All Locations</em>
            </MenuItem>
            {allLocations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name} ({location.type})
              </MenuItem>
            ))}
          </Select>
          <DateTimePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            ampm={false}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 180 },
                InputProps: {
                  endAdornment: startDate ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setStartDate(null)}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              },
              actionBar: {
                actions: ["accept"],
              },
            }}
          />
          <DateTimePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            ampm={false}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 180 },
                InputProps: {
                  endAdornment: endDate ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setEndDate(null)}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              },
              actionBar: {
                actions: ["accept"],
              },
            }}
          />
          <Button
            variant={showPastTrips ? "contained" : "outlined"}
            size="small"
            onClick={() => setShowPastTrips(!showPastTrips)}
            sx={{ height: 32 }}
          >
            {showPastTrips ? "Hide Past" : "Show Past"}
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {tableData.length} trips
          </Typography>
        </Stack>
      </LocalizationProvider>

      {/* Bulk Action Toolbar */}
      {selectedTripCount > 0 && (
        <BulkActionToolbar
          selectedCount={selectedTripCount}
          onBulkAssign={handleBulkAssign}
          onBulkUnassign={handleBulkUnassign}
          onBulkCancel={handleBulkCancel}
          onBulkDelete={handleBulkDelete}
          onClearSelection={() => setRowSelectionModel([])}
          canDelete={hasPermission(Permission.DELETE_TRIPS)}
        />
      )}

      {/* Data Grid */}
      <Box sx={{ flex: 1, width: "100%" }}>
        <DataGrid
          rows={tableData}
          columns={columns}
          getRowId={(row) => row.trip_id}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          density="compact"
          sx={{
            "& .MuiDataGrid-row": {
              minHeight: "32px !important",
              "&:nth-of-type(even)": {
                backgroundColor: "#fafbfc",
              },
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              py: 0.5,
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50 },
            },
          }}
          loading={loadingTripIds.size > 0}
        />
      </Box>

      {/* Create Trip Dialog */}
      <CreateTripDialog
        open={createTripDialogOpen}
        onClose={() => {
          setCreateTripDialogOpen(false);
          setSelectedTailNumberForNewTrip(null);
        }}
        onSuccess={async () => {
          setCreateTripDialogOpen(false);
          setSelectedTailNumberForNewTrip(null);
          // Refresh trips list
          if (activeFBO) {
            const allTrips = await tripService.listTripsWithTenantAndPlacement(
              activeFBO.id
            );
            const sortedTrips = allTrips.sort((a, b) => {
              const aDate = a.arrival_date
                ? new Date(a.arrival_date).getTime()
                : 0;
              const bDate = b.arrival_date
                ? new Date(b.arrival_date).getTime()
                : 0;
              return aDate - bDate;
            });
            setInitialTrips(sortedTrips);
            refreshTrips(sortedTrips);
          }
        }}
        simpleMode={!!selectedTailNumberForNewTrip}
        existingTenant={selectedTailNumberForNewTrip || undefined}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <ConfirmationDialog
          title="Delete Trips?"
          text={`Are you sure you want to permanently delete ${rowSelectionModel.length} trip${rowSelectionModel.length !== 1 ? "s" : ""}? This action cannot be undone.`}
          okText="Delete"
          okButtonColor="error"
          onCancel={() => setShowDeleteConfirmation(false)}
          onOk={handleConfirmDelete}
          onClose={() => setShowDeleteConfirmation(false)}
        />
      )}
    </Stack>
  );
};
